import React from 'react';
// import { Document, Page, Text, View, Image, Svg, Path } from '@react-pdf/renderer';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import FSCLogo from '../../assets/fsc-logo.png';
import ChevronRed from '../../assets/chevron-red.png';
import ChevronGreen from '../../assets/chevron-green.png';
import Compass from '../../assets/compass.png';
import { audit } from '../../config/audit.config';
import { report } from '../../config/report.config';
import styles from './styles';
import { IPerformAuditCriteriaData } from '../../types/auditdata';
import mapController from '../../controllers/MapController';
import { ICriterion } from '../../types/audit-config';
import { IReportConfigCriterion } from '../../types/report-config';
import { IReportProps } from '../../types/report';

const formatNumber = (number?: number) => {
  if (number) {
    var parts = number.toFixed(2).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (parts.length > 1 && Number(parts[1]) > 0) { // only want to return decimals if greater than .00
      return parts.join(".");
    } else {
      return parts[0];
    }
  } else {
    return 0
  }
}

const rgbToHex = (r: number, g: number, b: number, a?: number) => {
  if (a === 0) {
    return '#f2f2f2'; // color of legend wrapper
  } else {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }).join('')
  }
};

interface ReportProps extends Pick<IReportProps, 'map' | 'charts' | 'auditAreaCoordinates' | 'auditTreeLoss' | 'visibleLayers'> {
  mapScale?: number;
  date: string;
  auditAreaSize: number;
  performAuditMap: Map<string, IPerformAuditCriteriaData>
}

const FSCReport = ({ map, mapScale, charts, date, auditAreaCoordinates, auditAreaSize, auditTreeLoss, performAuditMap, visibleLayers }: ReportProps) => {
  const roundedArea = Math.round(auditAreaSize);
  const assessedCriteria = Array.from(performAuditMap.keys()).sort((a, b) => Number(a) - Number(b));
  let currentPrinciple: number;

  const renderHectar = (value: number, fontSize: number) => {
    return (
      <View style={styles.hectarCount}>
        <Text style={[{fontSize: fontSize, letterSpacing: '3pt'}]}>{formatNumber(value)} </Text>
        {!isNaN(value) && <Text style={[{fontSize: fontSize * 0.70}]}> ha</Text>}
      </View>
    )
  }

  const renderTreeCoverLoss = (value: number, fontSize: number, chevronHeight: string) => {
    return (
      <View style={styles.treeLossLegend}>
        <Image src={value > 0 ? ChevronRed : ChevronGreen} style={[styles.chevron, { height: chevronHeight, marginRight: chevronHeight }]} cache={true} />
        <Text style={[{marginTop: '5pt'}]}>{renderHectar(value, fontSize)}</Text>
      </View>
    );
  }

  const renderIntersectionArea = (area?: number, intersecting?: boolean) => {
    return <Text>{`${intersecting && area ? formatNumber(area) : '0'} ha`}</Text>
  }

  const renderLegend = (layerID: string) => {
    if (layerID.includes('raster-analysis')) {
      return (
        <>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#00FF00'}]} />
            <Text style={styles.mapLegendText}>Tree Cover Gain</Text>
          </View>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#ff0000'}]} />
            <Text style={styles.mapLegendText}>Tree Cover Loss</Text>
          </View>
        </>
      )
    } else if (layerID === 'hansen') {
      return (
        <View style={styles.mapLegend}>
          <View style={[styles.mapLegendIcon, { backgroundColor: '#FF1493'}]} />
          <Text style={styles.mapLegendText}>Tree Cover Loss</Text>
        </View>
      )
    } else if (layerID === 'tree-cover-loss-by-dominant-driver') {
      return (
        <>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#F21D36'}]} />
            <Text style={styles.mapLegendText}>Commodity Driven Deforestation</Text>
          </View>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#F4DB5A'}]} />
            <Text style={styles.mapLegendText}>Shifting Agriculture</Text>
          </View>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#2EBF71'}]} />
            <Text style={styles.mapLegendText}>Forestry</Text>
          </View>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#AD6828'}]} />
            <Text style={styles.mapLegendText}>Wildfire</Text>
          </View>
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#B236CC'}]} />
            <Text style={styles.mapLegendText}>Urbanization</Text>
          </View>
        </>
      )
    } else if (layerID.includes('riparian-boundary')) {
        return (
          <View style={styles.mapLegend}>
            <View style={[styles.mapLegendIcon, { backgroundColor: '#164297'}]} />
            <Text style={styles.mapLegendText}>Riparian Boundaries</Text>
          </View>
        )
    } else {
      const mapLayer = mapController._mapLayers.find(layer => layer.id === layerID);
      const myLayer = mapController._myLayers.find(layer => layer.id === layerID)
      if (mapLayer && mapLayer.renderer) {
        if (mapLayer.renderer.uniqueValueInfos) {
          return mapLayer.renderer.uniqueValueInfos.map((value: any )=> 
            <View key={mapLayer.id} style={styles.mapLegend}>
              <View 
                style={[styles.mapLegendIcon, { 
                  backgroundColor: `${rgbToHex(value.symbol.color.r, value.symbol.color.g, value.symbol.color.b, value.symbol.color.a)}`,
                  opacity: value.symbol.color.a,
                  border: `${value.symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(value.symbol.color.r, value.symbol.color.g, value.symbol.color.b)}`
                }]} />
              <Text style={styles.mapLegendText}>{value.value}</Text>
            </View>
          )
        } else if (mapLayer.renderer.symbol) {
          const symbol = mapLayer.renderer.symbol.outline ? mapLayer.renderer.symbol.outline : mapLayer.renderer.symbol.color;
          return (
            <View key={mapLayer.id} style={styles.mapLegend}>
              <View 
                style={[styles.mapLegendIcon, { 
                  backgroundColor: `${rgbToHex(symbol.color.r, symbol.color.g, symbol.color.b, symbol.color.a)}`,
                  border: `${symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(symbol.color.r, symbol.color.g, symbol.color.b)}`
                }]} />
              <Text style={styles.mapLegendText}>{mapLayer.title}</Text>
            </View>
          )
        }
      } else if (mapLayer && mapLayer.graphics && mapLayer.graphics.items[0] && mapLayer.graphics.items[0].symbol) {
        return (
          <View key={mapLayer.id} style={styles.mapLegend}>
            <View 
              style={[styles.mapLegendIcon, {
                backgroundColor: `${rgbToHex(mapLayer.graphics.items[0].symbol.color.r, mapLayer.graphics.items[0].symbol.color.g, mapLayer.graphics.items[0].symbol.color.b, mapLayer.graphics.items[0].symbol.color.a)}`,
                border: `${mapLayer.graphics.items[0].symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(mapLayer.graphics.items[0].symbol.outline.color.r, mapLayer.graphics.items[0].symbol.outline.color.g, mapLayer.graphics.items[0].symbol.outline.color.b)}`
              }]} />
            <Text style={styles.mapLegendText}>{mapLayer.title}</Text>
          </View>
        )
      }
      if (myLayer && myLayer.renderer) {
        if (myLayer.renderer.uniqueValueInfos) {
          return myLayer.renderer.uniqueValueInfos.map((value: any )=> 
            <View key={myLayer.id} style={styles.mapLegend}>
              <View 
                style={[styles.mapLegendIcon, { 
                  backgroundColor: `${rgbToHex(value.symbol.color.r, value.symbol.color.g, value.symbol.color.b, value.symbol.color.a)}`,
                  border: `${value.symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(value.symbol.color.r, value.symbol.color.g, value.symbol.color.b)}`
                }]} />
              <Text style={styles.mapLegendText}>{value.value}</Text>
            </View>
          )
        } else if (myLayer.renderer.symbol) {
          return (
            <View key={myLayer.id} style={styles.mapLegend}>
              <View 
                style={[styles.mapLegendIcon, {
                  backgroundColor: `${rgbToHex(myLayer.renderer.symbol.color.r, myLayer.renderer.symbol.color.g, myLayer.renderer.symbol.color.b, myLayer.renderer.symbol.color.a)}`,
                  border: `${myLayer.renderer.symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(myLayer.renderer.symbol.outline.color.r, myLayer.renderer.symbol.outline.color.g, myLayer.renderer.symbol.outline.color.b)}`
                }]} />
              <Text style={styles.mapLegendText}>{myLayer.title}</Text>
            </View>
          )
        }
      } else if (myLayer && myLayer.graphics && myLayer.graphics.items[0] && myLayer.graphics.items[0].symbol) {
        return (
          <View key={myLayer.id} style={styles.mapLegend}>
            <View 
              style={[styles.mapLegendIcon, {
                backgroundColor: `${rgbToHex(myLayer.graphics.items[0].symbol.color.r, myLayer.graphics.items[0].symbol.color.g, myLayer.graphics.items[0].symbol.color.b, myLayer.graphics.items[0].symbol.color.a)}`,
                border: `${myLayer.graphics.items[0].symbol.color.a === 0 ? '3pt' : '0pt'} solid ${rgbToHex(myLayer.graphics.items[0].symbol.outline.color.r, myLayer.graphics.items[0].symbol.outline.color.g, myLayer.graphics.items[0].symbol.outline.color.b)}`
              }]} />
            <Text style={styles.mapLegendText}>{myLayer.title}</Text>
          </View>
        )
      }
    }
  };

  const renderCriterion = (criterion: ICriterion) => {
    return (
      <View style={styles.criteriaWrapper}>
        <Text>{`Criteria ${criterion.criteria}`} - </Text>
        {renderCriterionDescription(report.criterion[criterion.criteria])}
      </View>
    )
  }

  const renderCriterionDescription = (criterion: IReportConfigCriterion) => {
    let list, items;
    if (criterion.list) {
      list = (
        <View style={styles.criterionList}>
          {
            criterion.list.map((string, index) =>
              <Text>{`${report.listLetters[index]}) ${string}`}</Text>
            )
          }
        </View>
      )
    }
    if (criterion.items) {
      items = (
        <View style={styles.criterionList}>
          {
            criterion.items.map(string =>
              <Text style={styles.criterionItem}>{string}</Text>
            )
          }
        </View>
      )
    }
    return (
      <View style={styles.criterionDescriptionWrapper}>
        <Text>{criterion.description}</Text>
        {list}
        {items}
      </View>
    )
  }

  const renderAnalysis = (assessment: IPerformAuditCriteriaData | undefined) => {
    const { principles } = audit;
    if (assessment) {
      const principleFromCriteria = Number(assessment.criteriaNumber.split('.')[0]);
      const principle = principles.find(principle => principle.principle === principleFromCriteria);
      if (principle) {
        const chart = charts.find(chart => chart.criteriaNumber === assessment.criteriaNumber);
        const criterion= principle.criterion.find(c => c.criteria === assessment.criteriaNumber);
        const assessmentView = (
          <View style={styles.assessment}>
            {(!currentPrinciple || (currentPrinciple !== principle.principle)) && <Text style={styles.assessmentPrinciple}>{`Principle ${principle.principle}: ${principle.title}`}</Text>}
            {criterion && renderCriterion(criterion)}
            <View style={styles.assessmentResults}>
              <View style={styles.resultsLeft}>
                <Text style={styles.assessmentResultsHeading}>Reference Layer:</Text>
                <Text style={styles.assessmentResultsValue}>
                  {performAuditMap.get(assessment.criteriaNumber)?.selectedLayer.title}
                </Text>
                <Text style={styles.assessmentResultsHeading}>Intersection Area:</Text>
                <Text style={[{marginTop: '5pt', marginBottom: '10pt', fontSize: 10}]}>
                  {renderIntersectionArea(performAuditMap.get(assessment.criteriaNumber)?.area, performAuditMap.get(assessment.criteriaNumber)?.intersecting)}
                </Text>
                <Text style={styles.assessmentResultsHeading}>2019 Tree Cover Loss:</Text>
                {findAnalyzedTreeCoverLoss(assessment.criteriaNumber)}
              </View>
              <View style={styles.resultsRight}>
                {chart && <Image src={chart.chart} style={styles.chart} cache={true} />}
              </View>
            </View>
          </View>
        )
        currentPrinciple = principle?.principle;
        return assessmentView;
      }
    }
  }

  const findAnalyzedTreeCoverLoss = (criterion: string) => {
    const chart = charts.find(chart => chart.criteriaNumber === criterion);
    let loss = 0;
    if (chart && chart.treeLoss) {
      loss = chart.treeLoss
    }
    return renderTreeCoverLoss(loss, 14, '5pt')
  }

  const renderPrincipleTables = () => {
    const { principles } = audit;
    return principles.map(principle => 
      <View key={principle.principle} style={styles.principleWrapper}>
        <Text style={styles.principleTitle}>
          {`Principle ${principle.principle}: ${principle.title}`}
        </Text>
        <View style={styles.principleTable}>
          <View style={styles.principleTableHeader}>
            <View style={[styles.principleTableColumn, { width: '15%'}]}>
              <Text >Criteria</Text>
            </View>
            <View style={[styles.principleTableColumn, { width: '15%'}]}>
              <Text>Analyzed</Text>
            </View>
            <View style={[styles.principleTableColumn, { width: '25%'}]}>
              <Text>Reference Layer</Text>
            </View>
            <View style={[styles.principleTableColumn, { width: '20%'}]}>
              <Text>Intersection Area</Text>
            </View>
            <View style={[styles.principleTableColumn, { width: '25%'}]}>
              <Text>2019 Tree Cover Loss</Text>
            </View>
          </View>
          <View style={styles.principleTableBody}>
            {principle.criterion.map(criterion =>
              <View key={criterion.criteria} style={styles.principleTableRow}>
                <View style={[styles.principleTableColumn, { width: '15%'}]}>
                  <Text>{criterion.criteria}</Text>
                </View>
                <View style={[styles.principleTableColumn, { width: '15%'}]}>
                  <Text>{assessedCriteria.includes(criterion.criteria) ? 'Yes' : 'No'}</Text>
                </View>
                <View style={[styles.principleTableColumn, { width: '25%'}]}>
                  <Text>{assessedCriteria.includes(criterion.criteria) ? performAuditMap.get(criterion.criteria)?.selectedLayer.title : 'N/A'}</Text>
                </View>
                <View style={[styles.principleTableColumn, { width: '20%'}]}>
                  {assessedCriteria.includes(criterion.criteria)
                    ? renderIntersectionArea(performAuditMap.get(criterion.criteria)?.area, performAuditMap.get(criterion.criteria)?.intersecting)
                    : <Text>N/A</Text>
                  }
                </View>
                <View style={[styles.principleTableColumn, { width: '25%'}]}>
                  {assessedCriteria.includes(criterion.criteria) ? findAnalyzedTreeCoverLoss(criterion.criteria) : <Text>N/A</Text>}
                </View>
              </View>
            )}
          </View>
        </View>
      </View>
    )
  }

  const renderFrontPage = () => {
    return (
      <Page size='A4' orientation='landscape' style={styles.page}>
        <View style={[styles.pageLeft, { width: '35%' }]}>
          <View style={styles.frontPageHeader}>
            <Image src={FSCLogo} style={styles.frontPageLogo} cache={true} />
            <Text style={styles.frontPageTitle}>Analysis Results</Text>
          </View>
          <View style={styles.frontPageInfo}>
            <Text style={styles.frontPageHeading}>Analysis Date:</Text>
            <Text style={styles.frontPageValue}>{date}</Text>
            <Text style={styles.frontPageHeading}>Analysis Area:</Text>
            <Text style={[{marginTop: '5pt', marginBottom: '20pt'}]}>{renderHectar(roundedArea, 24)}</Text>
            <Text style={styles.frontPageHeading}>2019 Tree Cover Loss:</Text>
            {renderTreeCoverLoss((Math.round(auditTreeLoss * 0.09)), 24, '10pt')}
          </View>
          <Text style={styles.footnote}>Tree Cover Loss values only consider the Hansen’s Tree Cover Loss data</Text>
        </View>
        {map !== '' && 
          <View style={[styles.pageRight, { width: '65%', position: 'relative' }]}>
            <Image src={map} style={styles.map} cache={true} />
            <Image src={Compass} style={styles.mapCompass} cache={true} />
            <View style={styles.mapLocation}>
              {auditAreaCoordinates &&
                <Text style={styles.mapCoordinates}>{`Coordinates [${Number(auditAreaCoordinates.longitude).toFixed(5)}, ${Number(auditAreaCoordinates.latitude).toFixed(5)}]`}</Text>
              }
              {mapScale &&
              <Text style={styles.mapCoordinates}>{`Map Scale [1: ${formatNumber(Math.round(mapScale))}]`}</Text>}
            </View>
            <View style={styles.mapLegendWrapper}>
              <Text style={styles.mapLegendTitle}>MAP LEGEND</Text>
              <View style={styles.legendItemsWrapper}>
                {
                  visibleLayers.map(layer => renderLegend(layer))
                }
              </View>
            </View>
          </View>
        }
      </Page>
    )
  }

  const renderSecondPage = () => {
    return (
      <Page size='A4' orientation='landscape' style={styles.page}>
        <View style={styles.pageLeft}>{renderPrincipleTables()}</View>
        <View style={styles.pageRight}>
          <Text style={styles.assessmentTitle}>Analysis</Text>
          {charts.length > 0 && (
            <View style={styles.assessmentsWrapper}>
              {renderAnalysis(performAuditMap.get(assessedCriteria[0]))}
              {(assessedCriteria[0] !== "9.1" && assessedCriteria[1] !== "9.1") && renderAnalysis(performAuditMap.get(assessedCriteria[1]))}
            </View>
          )}
        </View>
      </Page>
    )
  }

  const renderAnalysisPages = () => {
    const chartsToSkip = assessedCriteria.slice(0, 2).includes("9.1") ? 1 : 2;
    // Get the remaining charts (minus 1 or 2 depending on whether criterion 9 is one of the first 2)
    const remainingCriteria = assessedCriteria.slice(chartsToSkip, assessedCriteria.length);
    if (remainingCriteria.length > 0) {
      const numberOfPages = Math.ceil(remainingCriteria.length / 4);
      const pagesArray = [];
      let currentIndex = 0;
      let i = 0;
      while (i < numberOfPages) {
        pagesArray.push(
          <Page size='A4' orientation='landscape' style={styles.page}>
            <View style={styles.pageLeft}>
              {remainingCriteria[currentIndex] && (
                  renderAnalysis(performAuditMap.get(remainingCriteria[currentIndex++]))
                )}
              {(remainingCriteria[currentIndex - 1] !== "9.1" && (remainingCriteria[currentIndex] && remainingCriteria[currentIndex ] !== "9.1")) && (
                renderAnalysis(performAuditMap.get(remainingCriteria[currentIndex++]))
              )}
            </View>
            <View style={styles.pageRight}>
              {remainingCriteria[currentIndex] && (
                    renderAnalysis(performAuditMap.get(remainingCriteria[currentIndex++]))
                  )}
              {(remainingCriteria[currentIndex - 1] !== "9.1" && (remainingCriteria[currentIndex] && remainingCriteria[currentIndex] !== "9.1")) && (
                renderAnalysis(performAuditMap.get(remainingCriteria[currentIndex++]))
              )}
            </View>
          </Page>
        )
        // if we're on the last page, make sure we don't have any criteria left to display
        // (typically happens if we have criteria 10 when 9 falls on the right side of the page)
        if (i === numberOfPages - 1 && currentIndex < remainingCriteria.length) {
          // Don't increment pages
        } else {
          i++;
        }
      }
      return pagesArray;
    }
  };

  return (
    <Document>
      {renderFrontPage()}
      {renderSecondPage()}
      {renderAnalysisPages()}
    </Document>
  );
};

export default FSCReport;
